import { Fragment, forwardRef, useEffect, useRef, useState, type CSSProperties } from 'react'
import { flushSync } from 'react-dom'
import {
  Disc3, Drama, Film, Frame, Guitar, Heart, MicVocal, Music, PartyPopper, PersonStanding, ShoppingBag, Speech,
  type LucideIcon, FerrisWheel, Brain,
} from 'lucide-react'
import { IconButton, Row, useToast } from '../ui'
import { EventActions } from './EventActions'
import { heroIsVenue, heroUrl, posterUrl, signalFeatures, idsOf, tripLine } from '../lib/entries'
import { FORM_LABEL, classicalForm } from '../lib/classicalForm'
import {
  decideLine, isPermanent, kindLine, kindWord, leadingOf, plateFor, rowTitle, runProgress,
  whatLine, whereLine, type Leading,
} from '../lib/rowMeta'
import { useIsSaved, useSeries } from '../lib/queries'
import { placePoint, useRegionPlace } from '../lib/currentRegion'
import { currentRegion, useRegionsNow } from '../lib/regions'
import { clubRow } from '../lib/night'
import { kindWordShown, type Kind } from '../lib/browse'
import { useDismissEntry, useForgetEntry, useSaveEntry } from '../lib/mutations'
import type { FeedRow } from '../lib/types'
import { iso } from '../lib/calendar'
import type { ShowingLite } from './CardBody'
import { VenueLink } from './VenueLink'
import './EventRow.css'

// ---------------------------------------------------------------------------
// The genre floor (WP 6.9, item 3)
//
// `.ev-row__genre` keeps shrinking exactly as it always did — `min-width: 0`,
// `flex: 0 1 auto` — because kind and when are `flex: none` and there is
// nowhere else for a floor's worth of width to come from. What changes is
// only whether the shrunk box gets painted, and that has to be read off the
// box's real, already-laid-out width rather than asked of it: a `@container`
// query on this element made flexbox treat its own content as zero-sized for
// its parent's sizing purposes (`contain: inline-size`, which `container-
// type` implies), which is exactly what `flex-basis: auto` needs to shrink
// from — every genre on every row measured 0px, whether or not there was
// room. One shared `ResizeObserver` instead: many rows register the same
// instance rather than one each, and its callback toggles a class directly
// (`classList`, not React state) since this is a paint change, not a tree
// change, and a row list can be long.
// ---------------------------------------------------------------------------

/** The genre box's width, its own leading separator included (~15px of the
 *  12px caption face's "·" and margins), below which fewer than three
 *  letters and the ellipsis would be left: "O…" is the mark of a word, not
 *  a shorter one (a1-13). Was 22, which measured the separator as if it
 *  were text and let "O…" through. */
const GENRE_FLOOR = 46

let genreObserver: ResizeObserver | null = null

/** a1-13: the observer watches the kind *line*, not the genre box, and an
 *  emptied genre is `display: none` rather than `visibility: hidden`.
 *  Hidden-but-laid-out kept the box's width, so a dropped genre left a
 *  blank gap before the clock's separator ("Concert ·   · Sat 24 Oct" at
 *  360px). Watching the box itself could not take it out of the layout: a
 *  `display: none` box measures 0 and would never come back. The line's own
 *  width does not depend on the genre (it fills its column), so on each
 *  resize the genre is shown, measured where flexbox put it, and taken out
 *  again only if it is below the floor — one synchronous read per row per
 *  resize, before paint, so nothing flickers. */
function checkGenre(line: Element) {
  const g = line.querySelector<HTMLElement>('.ev-row__genre')
  if (!g) return
  g.classList.remove('is-empty')
  g.classList.toggle('is-empty', g.getBoundingClientRect().width < GENRE_FLOOR)
}

function watchGenre(el: HTMLElement): () => void {
  const line = el.parentElement
  if (!line) return () => {}
  if (!genreObserver) {
    genreObserver = new ResizeObserver(entries => {
      for (const entry of entries) checkGenre(entry.target)
    })
  }
  genreObserver.observe(line)
  return () => genreObserver?.unobserve(line)
}

/**
 * One event, in a list. Every list of events in the app is this.
 *
 * A composition over `ui/Row`, not a fifteenth primitive: the anatomy, the
 * swipe, the pointer capture and the leave motion are all `Row`'s, and this
 * decides only what goes in the three slots and what the gestures do. What it
 * says is `rowMeta.ts`'s, which `npm test` can see.
 *
 * **It owns the writes (E4).** Save from a row, from the sheet and from a
 * place page used to be three call sites with three sets of handlers, and two
 * of them wrote a taste signal the third did not. The row reads `entry_state`
 * itself — one shared query key, so it costs one fetch however many rows are
 * on screen — and calls the same three mutations everywhere. That is why
 * `EventRow` takes no `saved` and no `onSave`: there is nothing for a caller
 * to get wrong.
 */
export function EventRow({ e, showings, showHall, leavesOnSave, under, onOpen }: {
  e: FeedRow
  /** Every showing this row stands for. Read for the next time still ahead and
   *  for the cinema count; a screen that has not fetched them simply gets a row
   *  that states less. */
  showings?: ShowingLite[]
  /** A place page, where every row shares a building and the hall is the fact
   *  that tells them apart. */
  showHall?: boolean
  /** The screen drops this row from its list once it is saved (Browse's ranked
   *  events, WP 8.3). A save that makes the row vanish says so and offers the
   *  undo, the way a dismissal does; on a screen where the heart merely fills,
   *  the filled heart is the feedback and a toast would be noise. The caller
   *  decides because only the caller knows whether the row is about to leave. */
  leavesOnSave?: boolean
  /** The kind heading this row is drawn under, where there is one: the kind
   *  word is dropped where the heading already says it (`kindWordShown`,
   *  a1-13). Absent everywhere a row has no such heading. */
  under?: Exclude<Kind, 'all'> | null
  onOpen: (e: FeedRow) => void
}) {
  const saved = useIsSaved(e.id)
  // Nightlife N7: a party's name for the eyebrow. One shared query, like
  // `entry_state` above — every row asks, one request answers.
  const { data: seriesById } = useSeries()
  // A day trip's drive time is measured from the current region's pin (D5):
  // the home pin in Berlin, the Concord pin in Boston, and from nowhere in a
  // region the person has no place in.
  const home = placePoint(useRegionPlace())
  // C2.1: a day trip of the region being looked at — one shared query.
  const regions = useRegionsNow()

  const saveM = useSaveEntry()
  const forgetM = useForgetEntry()
  const dismissM = useDismissEntry()
  const toast = useToast()
  const [menu, setMenu] = useState(false)

  const lead = useRef<HTMLSpanElement>(null)
  const genre = useRef<HTMLSpanElement>(null)

  const ids = idsOf(e)
  const forget = () => forgetM.mutate({ ids })

  /** Heart and swipe both come through here. When the row is about to leave
   *  (`leavesOnSave`) it is a toast with an Undo, `forget` being the unsave
   *  the heart already uses — otherwise a tap makes the row disappear with
   *  nothing to say where it went. */
  const save = () => {
    saveM.mutate({ ids, features: signalFeatures(e) })
    if (leavesOnSave) toast({ text: 'Saved · in your Shortlist', action: { label: 'Undo', onClick: forget } })
  }

  /** F7: removal is a swipe and a toast, never a confirmation dialog. The undo
   *  is a delete rather than a third state — "I never decided" and "I decided
   *  no" are different things, which is what `useForgetEntry` is. */
  const dismiss = () => {
    dismissM.mutate({ ids, features: signalFeatures(e) })
    toast({ text: 'Hidden', action: { label: 'Undo', onClick: forget } })
  }

  /**
   * Open the sheet, carrying the picture across.
   *
   * The name is set on this row's thumbnail for the length of the transition
   * only, not while the sheet is open: two elements sharing a
   * `view-transition-name` at the same moment is invalid and the browser
   * abandons the whole transition, so the row claims the name, hands it to the
   * sheet's hero, and drops it again inside the same update callback. `flushSync` is what makes the callback
   * actually change the DOM — React would otherwise schedule the state update
   * for after the snapshot, and the transition would capture the same frame
   * twice.
   */
  function open() {
    const el = lead.current
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const doc = document as ViewTransitionDocument
    if (!el || reduced || !doc.startViewTransition) { onOpen(e); return }
    el.style.viewTransitionName = `hero-${e.id}`
    const t = doc.startViewTransition(() => {
      flushSync(() => onOpen(e))
      // Dropped inside the update callback, not after the transition: the old
      // snapshot is taken before this runs and the new one after it returns,
      // so clearing here is the only point at which exactly one element holds
      // the name in each. Clearing in `finished` instead left the row and the
      // sheet's hero both named in the new snapshot, which is the duplicate
      // the browser refuses — it logged
      // `Unexpected duplicate view-transition-name` and abandoned every
      // picture transition on Today. The `finally` below still runs, for the
      // transition that never reaches its callback.
      el.style.viewTransitionName = ''
    })
    void t.finished.catch(() => {}).finally(() => { el.style.viewTransitionName = '' })
  }

  const now = new Date()
  // Decided once, here, and read by the leading slot alone now: WP 5.8's
  // coupling between the plate and the meta line is gone with the meta line
  // (see `kindLine` in `rowMeta.ts` for why the date is no longer suppressed).
  const leading = leadingOf(e, { url: heroUrl(e, 112), isVenue: heroIsVenue(e), poster: posterUrl(e, 'w185'), now })

  // The four lines, from the four functions `npm test` can read. Nothing on
  // this row is composed here — see `rowMeta.ts`'s header and rule 1.
  //
  // The form is resolved by this component and handed down because
  // `classicalForm.ts` imports `./types` and `rowMeta.ts` may not import
  // anything; `leadingOf` takes its picture the same way and for the same
  // reason.
  const form = classicalForm(e)
  // `plateVisible` is this row's own answer, not the screen's (WP 6.9, item
  // 5) — and it is about what the reader can *see*, not about which leading
  // kind `leadingOf` returned. Only `'plate'` qualifies: a `'thumb'` also
  // draws `.ev-plate`, but `Leader`'s own `<img>` sits over it at
  // `inset: 0` and covers it completely (`EventRow.css`'s note on
  // `.ev-lead > img`, "a thumbnail covers it"), so a thumb row's clock is
  // not stated twice — it is stated once, on the picture nobody can read a
  // clock off, unless this stays `'plate'` alone. Caught on a render: an
  // earlier `leading.kind !== 'poster'` (true for both) suppressed the
  // clock on every thumb row too, and WP 6.1 measured 15 bare-plate rows
  // against 89 pictured ones, so that was the common case losing its time
  // entirely rather than the rare one reading it twice.
  const k = kindLine(e, { now, showings, form: form ? FORM_LABEL[form] : null, plateVisible: leading.kind === 'plate' })
  // Nightlife N7: what a club night adds, in the slots every row already
  // has — the host party where a film has its genre, the format and doors
  // where a concert has its programme, the lineup where a concert has who
  // is playing. `rowMeta.ts` returns nothing for a club in all three
  // (`whatLine`'s note: no facts column until now), so these fill empty
  // slots and replace nothing. Null for every other kind.
  const club = clubRow(e, seriesById)
  const genreText = club?.eyebrow ?? k.genre
  // Item 3's own watch, registered once there is a genre box to measure at
  // all — `EventRow.tsx`'s header explains why a `ResizeObserver` reads the
  // box rather than a `@container` query asking it to decide its own size.
  useEffect(() => {
    const el = genre.current
    return el ? watchGenre(el) : undefined
  }, [genreText])
  const what = club?.what ?? whatLine(e, { showings })
  const decide = club?.lineup ? [{ text: club.lineup }] : decideLine(e)
  const where = whereLine(e, { now, showings, showHall })
  // Blind spots A3: a day trip's town and drive, as the where line's tail —
  // the venue first, since rule 3 says nothing truncates it. An exhibition's
  // district is the town for a Potsdam venue, and the trip line names the
  // town, so the district goes rather than read "Potsdam · Potsdam · ~30 min".
  const trip = tripLine(e, home, currentRegion(regions), regions.towns)
  const town = (e.city ?? '').trim().toLowerCase()
  const whereParts = trip ? where.parts.filter(p => p.trim().toLowerCase() !== town) : where.parts

  // Not on a permanent display: the line already says "Permanent", and a bar
  // creeping across a four-year run is the flat line `runProgress` exists to
  // avoid — worse, it is a second answer to "how long have I got" that
  // disagrees with the first.
  const bar = e.type === 'exhibition' && !isPermanent(e, now)
    ? runProgress(e, iso(now))
    : null

  return (
    <>
      <Row
        /* `--card` is what says this row draws the five-line card rather than
           the one-line row `ui/Row` is written for. `Hidden.tsx` and
           `Today.tsx`'s `PlateRow` are `.ev-row` too and pass neither slot a
           stack; `EventRow.css` explains what the modifier buys. */
        className="ev-row ev-row--card"
        leading={<Leader ref={lead} e={e} leading={leading} />}
        title={
          /* The kind line sits *above* the title, which `ui/Row` has no slot
             for — so the title slot carries both and `EventRow.css` takes its
             box out of the layout (`display: contents`), exactly as the meta
             slot below. A third slot on the primitive for one caller would be
             a change in six other components' file. */
          <>
            <span className="ev-row__kindline">
              {/* The same fact in two registers: a hue you read a list by and
                  a word you read a row by. `kindWord` decides both — the dot
                  follows `hueOf`, which branches identically, so the two
                  cannot drift. */}
              <span className="ev-row__dot" style={{ background: k.dot }} aria-hidden="true" />
              {kindWordShown(k.kind, under) && <b className="ev-row__kind">{k.kind}</b>}
              {/* A film's first genre, a concert's form. Absent rather than
                  empty on everything else: a separator either side of nothing
                  is the row claiming a fact it has not got. `ref={genre}` is
                  what the module-level `ResizeObserver` above watches — see
                  its note and `EventRow.css`'s on why a lone ellipsis is a
                  third case, not a shorter version of showing the genre. */}
              {genreText && <span ref={genre} className="ev-row__genre">{genreText}</span>}
              {k.when && <b className="ev-row__when">{k.when}</b>}
            </span>
            <span className="ev-row__title">{rowTitle(e)}</span>
          </>
        }
        meta={
          <>
            {what && <span className="ev-row__what">{what}</span>}
            {decide.length > 0 && (
              /* The one line that changes by kind, and the reason the card
                 exists: scores for a film, who is playing for a concert, who
                 made it for a production, the gallery's own words for a show.
                 Up to two lines (rule 3) and never three. */
              <span className="ev-row__decide">
                {decide.map((part, i) => (
                  <Fragment key={i}>
                    {i > 0 && <span className="ev-row__sep" aria-hidden="true">{' · '}</span>}
                    {part.strong ? <b>{part.text}</b> : part.text}
                  </Fragment>
                ))}
              </span>
            )}
            {/* Rule 3: **nothing truncates the venue.** So this line wraps
                rather than ellipsising — the complaint that opened the package
                was "Concert · 20:00 · Philharm…", and a second line of where
                is the honest answer where the name really is that long. The
                tail is what a narrow screen loses room for, and it is behind
                the venue for that reason. */}
            <span className="ev-row__where">
              {where.venue && (
                <b className="ev-row__venue">
                  {/* a1-28: its own tap target — the place page — inside a
                      row that is otherwise one press. */}
                  {where.placeId ? <VenueLink id={where.placeId}>{where.venue}</VenueLink> : where.venue}
                </b>
              )}
              {/* The other cinemas, counted and not named: a film plays four
                  buildings and the row has room for one. */}
              {where.more > 0 && <span className="ev-row__more">{` +${where.more}`}</span>}
              {whereParts.length > 0 && (
                <span className="ev-row__wfact">{whereParts.join(' · ')}</span>
              )}
              {/* Its own box: the line is prose ("by car, est."), and the
                  where line's tabular figures set a comma and a full stop
                  a figure wide — "car , est ." on the first render. */}
              {trip && <span className="ev-row__wfact ev-row__trip">{trip}</span>}
            </span>
            {/* The one thing of the nine card shapes worth keeping: an
                exhibition's only real question is whether there is still
                time, and the bar answers it without a word. */}
            {bar && (
              <span className="ev-row__bar" aria-hidden="true">
                <i style={{ width: `${bar.pct}%` }} />
              </span>
            )}
          </>
        }
        trailing={
          /* The brief's 32px round button, at the top right of the text.
             `IconButton` is 44x44 because a tap target is 44x44 — NEXT.md
             has the grocery stepper's 28px squares filed as a bug — so the
             circle is painted at 32 and the hit area stays 44 around it
             (`EventRow.css`). `EventRow.css` also takes this out of the
             flex row and pins it to the top corner, which is why only the
             kind line stops short of it and the title does not. */
          <IconButton
            size="sm"
            className="ev-row__save"
            label={saved ? `Remove ${rowTitle(e)} from saved` : `Save ${rowTitle(e)}`}
            aria-pressed={saved}
            onClick={() => (saved ? forget() : save())}
          >
            <Heart size={16} fill={saved ? 'currentColor' : 'none'} />
          </IconButton>
        }
        onPress={open}
        swipe={{
          // Absent once the row is saved: a gesture whose action is already
          // true should spring back with no backdrop rather than promise
          // something and do nothing, which is what `Row` does with an
          // undefined direction.
          right: saved ? undefined : { label: 'Save', onAction: save },
          // `leaves` means "this row is about to be unmounted", and it is:
          // every list that draws these filters on `entry_state`, so the
          // optimistic dismissal takes the row out from under it.
          left: { label: 'Not for me', onAction: dismiss, leaves: true },
        }}
        onLongPress={() => setMenu(true)}
      />
      {menu && (
        <EventActions
          e={e}
          saved={saved}
          onClose={() => setMenu(false)}
          onSave={save}
          onForget={forget}
          onDismiss={dismiss}
        />
      )}
    </>
  )
}

/**
 * One glyph per `kindWord`, so the plate says what kind of thing this is before
 * any of its words are read.
 *
 * Picked once, here, and keyed on the ten words rather than on `e.type` — the
 * dot, the word and the glyph are then three registers of one decision
 * (`kindWord`) instead of three lists to keep in step. `Record` over the union
 * means a new word is a compile error rather than a row with no glyph.
 *
 * Chosen to be told apart at 20px, which is the whole constraint. An opera is
 * the sung voice (`MicVocal`) and a talk is a spoken one (`Speech`), because
 * two microphones at that size are one microphone twice; `PartyPopper` for an
 * opening is `openingLine`'s own point in `entries.ts` — the row is a party,
 * not another exhibition — and is why Exhibition and Opening are worth two
 * words at all.
 */
const KIND_GLYPH: Record<ReturnType<typeof kindWord>, LucideIcon> = {
  Concert: Music,
  Gig: Guitar,
  Opera: MicVocal,
  Dance: PersonStanding,
  Theatre: Drama,
  Exhibition: Frame,
  Opening: PartyPopper,
  // A festival is a fair (30 Sept): the wheel, because the popper is the
  // opening's and a tent at 20px is a triangle.
  Festival: FerrisWheel,
  Film: Film,
  Talk: Speech,
  Market: ShoppingBag,
  Club: Disc3,
  // Nightlife N6: a pub quiz — the thinking, not the pint, since a glass at
  // 20px is the market's bag's shape again.
  Quiz: Brain,
}

/**
 * The 60x88 leading slot: one of three, and never a photograph of the building
 * (D7). `leadingOf` decides; this draws.
 *
 * Taller than it is wide since WP 5.1 and 60x88 since WP 6.1 — near enough a
 * film poster's own 2:3 that a poster fills the slot instead of sitting in the
 * middle of it with paper showing above and below, and 88 + 12 + 12 is the
 * brief's 112px minimum row. A film is recognisable at a glance by its poster
 * and by nothing else, which is the reason for the shape.
 *
 * A picture that fails is the plate, not a broken-image icon: `onError` falls
 * back once to the original file — a rendition that the backfill has not
 * reached 404s — and then gives up, the same three stages `Hero` uses.
 *
 * `leading` is the caller's own `leadingOf` result, passed down rather than
 * recomputed here — a second call with the same inputs is a second place for
 * the two to drift. Recomputed only on `failed`, which `leading` could not
 * have known.
 */
const Leader = forwardRef<HTMLSpanElement, { e: FeedRow; leading: Leading }>(function Leader({ e, leading }, ref) {
  const [failed, setFailed] = useState(false)
  const now = new Date()
  const kind: Leading = failed ? leadingOf(e, { now }) : leading

  // The plate is the backdrop for a thumbnail, not only the answer when there
  // is no picture. Most of these are hotlinked from the venue's own server and
  // arrive after the row does, and a bare grey square is a hole in the list for
  // as long as that takes; a plate that is then covered is the same row either
  // way. It is also what is left when the picture fails, which is why `onError`
  // only has to take the image away. A poster gets no plate behind it — it does
  // not fill the slot, so the plate's own text would show around its edges.
  const plate = kind.kind === 'poster' ? null : plateFor(e, now)
  const Glyph = KIND_GLYPH[kindWord(e)]

  return (
    <span
      ref={ref}
      className={`ev-lead ev-lead--${kind.kind}`}
      style={plate ? ({ '--ev-hue': plate.hue } as CSSProperties) : undefined}
    >
      {plate && (
        <span className="ev-plate">
          {/* Three things in the plate since WP 5.1, not two: the kind, then
              the hour, then the date. The glyph is what a row with no picture
              has instead of one — it says concert or exhibition at the same
              distance a poster says film. */}
          <Glyph className="ev-plate__glyph" size={20} strokeWidth={1.5} aria-hidden="true" />
          <b>{plate.big}</b>
          {plate.small && <i>{plate.small}</i>}
        </span>
      )}
      {kind.kind !== 'plate' && (
        <img
          src={kind.url} alt="" width={60} height={88}
          loading="lazy" decoding="async"
          onError={() => setFailed(true)}
        />
      )}
    </span>
  )
})

/** `startViewTransition` is Baseline in Chromium and absent from TypeScript's
 *  DOM library at 5.6, so it is declared where it is used rather than in an
 *  ambient file — the feature is behind a capability check anyway, because
 *  Safari 18 and Firefox do not have it and a row must open there too. */
type ViewTransitionDocument = Document & {
  startViewTransition?: (cb: () => void) => { finished: Promise<void> }
}
